import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, CloseIcon, AlertIcon, InfoIcon } from './icons';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  message: string;
  type: ToastType;
  onClose: (id: string) => void;
  duration?: number;
}

const toastConfig = {
  success: { icon: <CheckIcon />, bg: 'bg-green-500', text: 'text-white' },
  error: { icon: <CloseIcon />, bg: 'bg-red-500', text: 'text-white' },
  warning: { icon: <AlertIcon />, bg: 'bg-amber-500', text: 'text-white' },
  info: { icon: <InfoIcon />, bg: 'bg-blue-500', text: 'text-white' },
};

const Toast: React.FC<ToastProps> = ({ id, message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);
    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  const config = toastConfig[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
      layout
      className={`flex items-center w-full max-w-sm p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 border-l-4 ${type === 'success' ? 'border-green-500' :
        type === 'error' ? 'border-red-500' :
          type === 'warning' ? 'border-amber-500' : 'border-blue-500'
        }`}
      role="alert"
    >
      <div className={`inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg ${config.bg} ${config.text}`}>
        {config.icon}
      </div>
      <div className="ml-3 text-sm font-normal text-gray-800 dark:text-gray-200">{message}</div>
      <button
        type="button"
        className="ml-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
        onClick={() => onClose(id)}
        aria-label="Close"
      >
        <span className="sr-only">Close</span>
        <CloseIcon />
      </button>
    </motion.div>
  );
};

export default Toast;
